<template>
  <div class="auth-card">
    <div class="auth-card__brand">
      <GlowRing :size="132" :rings="2" arc spin>
        <span class="auth-card__logo"><AppIcon name="eye" :size="34" /></span>
      </GlowRing>
      <h1 class="auth-card__title">Гляделки</h1>
      <p class="auth-card__subtitle">Смотри в глаза. Побеждай.</p>
    </div>

    <GlassCard radius="xl" padding="lg" class="auth-card__panel">
      <div v-if="notice" class="auth-card__notice" role="alert">
        <AppIcon name="warning" :size="20" />
        <span>{{ notice }}</span>
        <button v-if="retryable" type="button" class="auth-card__retry" @click="emit('retry')">Повторить</button>
      </div>

      <h2 class="auth-card__heading">{{ mode === "login" ? "Вход" : "Регистрация" }}</h2>

      <form class="auth-card__form" novalidate @submit.prevent="submit">
        <label class="auth-card__field">
          <span class="auth-card__label">Логин</span>
          <input
            v-model.trim="login"
            class="cb-input"
            type="text"
            name="login"
            :placeholder="mode === 'login' ? 'Введите логин' : 'Придумайте логин'"
            autocomplete="username"
            autocapitalize="off"
            spellcheck="false"
          />
        </label>
        <label class="auth-card__field">
          <span class="auth-card__label">Пароль</span>
          <input
            v-model="password"
            class="cb-input"
            type="password"
            name="password"
            :placeholder="mode === 'login' ? 'Введите пароль' : 'Придумайте пароль'"
            :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
          />
        </label>
        <label v-if="mode === 'register'" class="auth-card__field">
          <span class="auth-card__label">Повторите пароль</span>
          <input
            v-model="passwordConfirm"
            class="cb-input"
            type="password"
            name="password-confirm"
            placeholder="Повторите пароль"
            autocomplete="new-password"
          />
        </label>

        <p v-if="error" class="auth-card__error">{{ error }}</p>

        <AppButton type="submit" size="md" block :loading="submitting">
          {{ mode === "login" ? "Продолжить" : "Создать аккаунт" }}
        </AppButton>
      </form>

      <p class="auth-card__switch">
        <template v-if="mode === 'login'">
          Нет аккаунта?
          <button type="button" class="auth-card__link" @click="switchMode('register')">Зарегистрироваться</button>
        </template>
        <template v-else>
          Уже есть аккаунт?
          <button type="button" class="auth-card__link" @click="switchMode('login')">Войти</button>
        </template>
      </p>
    </GlassCard>
  </div>
</template>

<script setup>
/**
 * AuthCard — вход/регистрация по логину и паролю (когда приложение открыто не из Telegram
 * или авторизация через Telegram не удалась).
 *
 * <AuthCard :notice="text" retryable @retry="authTelegram" @authenticated="finishAuth" />
 * emits: authenticated(token: string), retry
 */
import { ref } from "vue";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import GlassCard from "@/components/ui/GlassCard.vue";
import GlowRing from "@/components/ui/GlowRing.vue";
import { credentialsAuth, registerUser } from "@/services/api";

defineProps({
  /** Сообщение над формой (например, «Не удалось войти через Telegram»). */
  notice: { type: String, default: "" },
  /** Показать кнопку «Повторить» в сообщении. */
  retryable: { type: Boolean, default: false },
});

const emit = defineEmits(["authenticated", "retry"]);

const mode = ref("login");
const login = ref("");
const password = ref("");
const passwordConfirm = ref("");
const error = ref("");
const submitting = ref(false);

function switchMode(next) {
  mode.value = next;
  error.value = "";
  password.value = "";
  passwordConfirm.value = "";
}

function validate() {
  if (!login.value || !password.value || (mode.value === "register" && !passwordConfirm.value)) {
    return mode.value === "login" ? "Заполните логин и пароль" : "Заполните все поля";
  }
  if (mode.value === "register") {
    if (login.value.length < 3) return "Логин должен быть не короче 3 символов";
    if (password.value.length < 6) return "Пароль должен быть не короче 6 символов";
    if (password.value !== passwordConfirm.value) return "Пароли не совпадают";
  }
  return "";
}

async function submit() {
  error.value = validate();
  if (error.value) return;
  submitting.value = true;
  try {
    const token =
      mode.value === "login"
        ? await credentialsAuth(login.value, password.value)
        : await registerUser(login.value, password.value);
    if (!token || typeof token !== "string") throw new Error("empty token");
    password.value = "";
    passwordConfirm.value = "";
    emit("authenticated", token);
  } catch (e) {
    if (mode.value === "login") {
      error.value = e && e.status === 0 ? e.message : "Неверный логин или пароль";
    } else if (e && e.status === 409) {
      error.value = "Такой логин уже занят";
    } else if (e && e.status === 0) {
      error.value = e.message;
    } else {
      error.value = (e && e.serverMessage && e.status === 400 && e.serverMessage) || "Не удалось зарегистрироваться";
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.auth-card {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.auth-card__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.auth-card__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--cb-surface-2);
  border: 1px solid var(--cb-lime-border);
  color: var(--cb-lime);
  box-shadow: var(--cb-glow);
}

.auth-card__title {
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.auth-card__subtitle {
  color: var(--cb-text-2);
  font-size: 15px;
}

.auth-card__panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.auth-card__notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--cb-radius-md);
  background: var(--cb-danger-soft);
  border: 1px solid rgba(255, 107, 90, 0.35);
  color: var(--cb-text);
  font-size: 14px;
  line-height: 1.35;
}

.auth-card__notice > span {
  flex: 1 1 auto;
}

.auth-card__retry {
  flex-shrink: 0;
  padding: 6px 10px;
  border: 1px solid var(--cb-lime-border);
  border-radius: 10px;
  background: transparent;
  color: var(--cb-lime);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.auth-card__heading {
  font-size: 22px;
  font-weight: 700;
}

.auth-card__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.auth-card__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.auth-card__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--cb-text-2);
}

.auth-card__error {
  margin: -4px 0 0;
  color: var(--cb-danger);
  font-size: 14px;
}

.auth-card__switch {
  text-align: center;
  font-size: 14px;
  color: var(--cb-text-2);
}

.auth-card__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--cb-lime);
  font-weight: 700;
  cursor: pointer;
}
</style>
